import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate, useLocation } from 'react-router-dom';

const Header = ({ onToggleSidebar, onCollapseSidebar, sidebarCollapsed }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme, isDark, logoUrl, logoAnimation } = useTheme();
  const [logoError, setLogoError] = useState(false);

  // Reset logo error when URL changes
  useEffect(() => {
    setLogoError(false);
  }, [logoUrl]);
  const navigate = useNavigate();
  const location = useLocation();

  const [profileOpen, setProfileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const profileRef = useRef(null);
  const searchRef = useRef(null);
  const searchInputRef = useRef(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchOpen(false);
        setSearchQuery('');
        setSearchResults([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when opened
  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  // Keyboard shortcut: Ctrl+K to open search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      }
      if (e.key === 'Escape') {
        setSearchOpen(false);
        setProfileOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Quick navigation search
  const quickNavItems = React.useMemo(() => {
    const basePath = `/${user?.role?.toLowerCase()}`;
    const items = {
      superadmin: [
        { path: `${basePath}/dashboard`, label: 'Dashboard', icon: 'bi-speedometer2' },
        { path: `${basePath}/user-approvals`, label: 'User Approvals', icon: 'bi-people-fill' },
        { path: `${basePath}/subscription-plans`, label: 'Subscription Plans', icon: 'bi-box' },
        { path: `${basePath}/settings`, label: 'Settings', icon: 'bi-gear' },
      ],
      admin: [
        { path: `${basePath}/home`, label: 'Home', icon: 'bi-house-door' },
        { path: `${basePath}/dashboard`, label: 'Dashboard', icon: 'bi-speedometer2' },
        { path: `${basePath}/book-appointment`, label: 'Book Appointment', icon: 'bi-calendar-plus' },
        { path: `${basePath}/appointments`, label: 'Appointments', icon: 'bi-calendar-check' },
        { path: `${basePath}/doctors`, label: 'Doctors', icon: 'bi-person-badge' },
        { path: `${basePath}/patients`, label: 'Patients', icon: 'bi-people' },
        { path: `${basePath}/staff`, label: 'Support', icon: 'bi-headset' },
        { path: `${basePath}/reports`, label: 'Reports', icon: 'bi-bar-chart' },
        { path: `${basePath}/business-settings`, label: 'Settings', icon: 'bi-gear' },
      ],
      doctor: [
        { path: `${basePath}/dashboard`, label: 'Dashboard', icon: 'bi-speedometer2' },
        { path: `${basePath}/appointments`, label: 'Appointments', icon: 'bi-calendar-check' },
        { path: `${basePath}/patients`, label: 'Patients', icon: 'bi-people' },
        { path: `${basePath}/settings`, label: 'Settings', icon: 'bi-gear' },
      ],
      staff: [
        { path: `${basePath}/home`, label: 'Home', icon: 'bi-house-door' },
        { path: `${basePath}/dashboard`, label: 'Dashboard', icon: 'bi-speedometer2' },
        { path: `${basePath}/appointments`, label: 'Appointments', icon: 'bi-calendar-check' },
        { path: `${basePath}/patients`, label: 'Patients', icon: 'bi-people' },
        { path: `${basePath}/settings`, label: 'Settings', icon: 'bi-gear' },
      ],
      patient: [
        { path: `${basePath}/dashboard`, label: 'Dashboard', icon: 'bi-speedometer2' },
        { path: `${basePath}/book-appointment`, label: 'Book Appointment', icon: 'bi-calendar-plus' },
        { path: `${basePath}/appointments`, label: 'My Appointments', icon: 'bi-calendar-check' },
        { path: `${basePath}/profile`, label: 'My Profile', icon: 'bi-person-circle' },
      ],
    };
    return items[user?.role] || [];
  }, [user?.role]);

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    const filtered = quickNavItems.filter((item) => item.label.toLowerCase().includes(query.toLowerCase()));
    setSearchResults(filtered);
  };

  const navigateToResult = (path) => {
    navigate(path);
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  // Get current page title from path
  const getPageTitle = () => {
    const segments = location.pathname.split('/').filter(Boolean);
    if (segments.length < 2) return 'Dashboard';
    const page = segments[segments.length - 1];
    return page
      .split('-')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  };

  // Get breadcrumb
  const getBreadcrumb = () => {
    const segments = location.pathname.split('/').filter(Boolean);
    if (segments.length < 2) return [];
    return segments.map((seg, i) => ({
      label: seg
        .split('-')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' '),
      path: '/' + segments.slice(0, i + 1).join('/'),
      isLast: i === segments.length - 1,
    }));
  };

  const getUserInitials = () => {
    const name = user?.full_name || user?.name || 'U';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getUserPhotoUrl = () => {
    if (!user?.photo) return null;
    const base = process.env.REACT_APP_API_URL || 'http://localhost/qms_reactjs_php/apis';
    return `${base}/uploads/doctors/${user.photo}`;
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const handleLogout = async () => {
    setProfileOpen(false);
    await logout();
  };

  const breadcrumbs = getBreadcrumb();

  return (
    <header className="nx-header">
      {/* Animated top accent line */}
      <div className="nx-header-accent"></div>

      <div className="nx-header-inner">
        {/* Left Section */}
        <div className="nx-header-left">
          {/* Mobile hamburger */}
          <button className="nx-hamburger d-lg-none" onClick={onToggleSidebar} aria-label="Toggle menu">
            <span className="nx-hamburger-line"></span>
            <span className="nx-hamburger-line"></span>
            <span className="nx-hamburger-line"></span>
          </button>

          {/* Desktop collapse toggle */}
          <button
            className="nx-collapse-toggle d-none d-lg-flex"
            onClick={onCollapseSidebar}
            aria-label="Collapse sidebar"
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <i className={`bi ${sidebarCollapsed ? 'bi-layout-sidebar-inset' : 'bi-layout-sidebar'}`}></i>
          </button>

          {/* Mobile Logo */}
          <div className="nx-header-logo d-lg-none">
            {logoUrl && !logoError ? (
              <img
                src={logoUrl}
                alt="Logo"
                className={`nx-header-logo-img logo-anim-${logoAnimation || 'none'}`}
                onError={() => setLogoError(true)}
              />
            ) : (
              <span className="nx-center-brand">QMS</span>
            )}
          </div>

          {/* Page Info */}
          <div className="nx-page-info">
            <h1 className="nx-page-title">{getPageTitle()}</h1>
            <nav className="nx-breadcrumb" aria-label="breadcrumb">
              {breadcrumbs.map((crumb, i) => (
                <React.Fragment key={crumb.path}>
                  {i > 0 && <span className="nx-breadcrumb-sep">/</span>}
                  {crumb.isLast ? (
                    <span className="nx-breadcrumb-current">{crumb.label}</span>
                  ) : (
                    <button className="nx-breadcrumb-link" onClick={() => navigate(crumb.path)}>
                      {crumb.label}
                    </button>
                  )}
                </React.Fragment>
              ))}
            </nav>
          </div>
        </div>

        {/* Right Section */}
        <div className="nx-header-right">
          {/* Emergency Button - Patient Only */}
          {user?.role === 'patient' && (
            <button
              className="nx-header-btn nx-emergency-btn"
              onClick={() => navigate('/patient/emergency-contacts')}
              title="Emergency Contacts"
            >
              <svg viewBox="0 0 100 100" width="28" height="28" className="nx-emergency-icon">
                {/* Siren rays */}
                <line x1="50" y1="2" x2="50" y2="14" stroke="#e60000" strokeWidth="4" strokeLinecap="round" />
                <line x1="25" y1="10" x2="30" y2="20" stroke="#e60000" strokeWidth="4" strokeLinecap="round" />
                <line x1="75" y1="10" x2="70" y2="20" stroke="#e60000" strokeWidth="4" strokeLinecap="round" />
                <line x1="10" y1="28" x2="20" y2="32" stroke="#e60000" strokeWidth="4" strokeLinecap="round" />
                <line x1="90" y1="28" x2="80" y2="32" stroke="#e60000" strokeWidth="4" strokeLinecap="round" />
                <line x1="6" y1="48" x2="17" y2="46" stroke="#e60000" strokeWidth="4" strokeLinecap="round" />
                <line x1="94" y1="48" x2="83" y2="46" stroke="#e60000" strokeWidth="4" strokeLinecap="round" />
                {/* Siren dome */}
                <path d="M22 62 Q22 22 50 22 Q78 22 78 62 Z" fill="#e60000" />
                {/* Dome shine */}
                <path d="M30 60 Q30 28 48 26 Q36 28 36 60 Z" fill="rgba(255,255,255,0.2)" />
                {/* Black cross on dome */}
                <rect x="46" y="32" width="8" height="22" rx="1.5" fill="#1a1a1a" />
                <rect x="38" y="39" width="24" height="8" rx="1.5" fill="#1a1a1a" />
                {/* Dark base */}
                <rect x="18" y="62" width="64" height="10" rx="3" fill="#2a2a2a" />
                {/* Red EMERGENCY banner */}
                <rect x="14" y="78" width="72" height="14" rx="2" fill="#e60000" />
                <text
                  x="50"
                  y="89"
                  textAnchor="middle"
                  fill="#1a1a1a"
                  fontSize="10"
                  fontWeight="bold"
                  fontFamily="Arial, sans-serif"
                >
                  EMERGENCY
                </text>
              </svg>
              <span className="nx-emergency-label">Emergency</span>
            </button>
          )}

          {/* Search Button */}
          <div className="nx-search-wrapper" ref={searchRef}>
            <button
              className="nx-header-btn nx-search-trigger"
              onClick={() => setSearchOpen(!searchOpen)}
              title="Search (Ctrl+K)"
            >
              <i className="bi bi-search"></i>
              <span className="nx-search-kbd d-none d-md-inline-flex">
                <kbd>&#8984;K</kbd>
              </span>
            </button>

            {/* Search Dropdown */}
            {searchOpen && (
              <div className="nx-search-dropdown">
                <div className="nx-search-input-wrap">
                  <i className="bi bi-search nx-search-icon"></i>
                  <input
                    ref={searchInputRef}
                    type="text"
                    className="nx-search-input"
                    placeholder="Search pages..."
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && searchResults.length > 0) {
                        navigateToResult(searchResults[0].path);
                      }
                    }}
                  />
                  <kbd className="nx-search-esc">ESC</kbd>
                </div>
                {searchQuery && (
                  <div className="nx-search-results">
                    {searchResults.length > 0 ? (
                      searchResults.map((item) => (
                        <button
                          key={item.path}
                          className={`nx-search-result-item ${location.pathname === item.path ? 'active' : ''}`}
                          onClick={() => navigateToResult(item.path)}
                        >
                          <i className={`bi ${item.icon}`}></i>
                          <span>{item.label}</span>
                          <i className="bi bi-arrow-return-left nx-result-enter"></i>
                        </button>
                      ))
                    ) : (
                      <div className="nx-search-empty">
                        <i className="bi bi-emoji-frown"></i>
                        <span>No pages found</span>
                      </div>
                    )}
                  </div>
                )}
                {!searchQuery && (
                  <div className="nx-search-results">
                    <div className="nx-search-hint">Quick Navigation</div>
                    {quickNavItems.slice(0, 5).map((item) => (
                      <button
                        key={item.path}
                        className={`nx-search-result-item ${location.pathname === item.path ? 'active' : ''}`}
                        onClick={() => navigateToResult(item.path)}
                      >
                        <i className={`bi ${item.icon}`}></i>
                        <span>{item.label}</span>
                        <i className="bi bi-arrow-return-left nx-result-enter"></i>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Theme Toggle */}
          <button
            className="nx-header-btn nx-theme-toggle"
            onClick={toggleTheme}
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            <div className="nx-theme-icon-wrap">
              <i className={`bi ${isDark ? 'bi-sun-fill' : 'bi-moon-stars-fill'}`}></i>
            </div>
          </button>

          {/* Divider */}
          <div className="nx-header-divider d-none d-md-block"></div>

          {/* Profile */}
          <div className="nx-profile-wrapper" ref={profileRef}>
            <button
              className={`nx-profile-trigger ${profileOpen ? 'open' : ''}`}
              onClick={() => setProfileOpen(!profileOpen)}
            >
              <div className="nx-avatar">
                {getUserPhotoUrl() ? (
                  <img
                    src={getUserPhotoUrl()}
                    alt="profile"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                ) : null}
                <span style={{ display: getUserPhotoUrl() ? 'none' : 'block' }}>{getUserInitials()}</span>
                <span className="nx-avatar-status"></span>
              </div>
              <div className="nx-profile-info d-none d-md-block">
                <span className="nx-profile-name">{user?.full_name || user?.name || 'User'}</span>
                <span className="nx-profile-role"> - {user?.role_name || user?.role}</span>
              </div>
              <i className={`bi bi-chevron-down nx-profile-chevron d-none d-md-block ${profileOpen ? 'open' : ''}`}></i>
            </button>

            {/* Profile Dropdown */}
            {profileOpen && (
              <div className="nx-profile-dropdown">
                <div className="nx-dropdown-header">
                  <div className="nx-dropdown-avatar">
                    {getUserPhotoUrl() ? (
                      <img
                        src={getUserPhotoUrl()}
                        alt="profile"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                    ) : null}
                    <span style={{ display: getUserPhotoUrl() ? 'none' : 'block' }}>{getUserInitials()}</span>
                  </div>
                  <div>
                    <div className="nx-dropdown-name">{user?.full_name || user?.name}</div>
                    <div className="nx-dropdown-email">{user?.email || user?.mobile}</div>
                  </div>
                </div>
                <div className="nx-dropdown-divider"></div>
                <div className="nx-dropdown-body">
                  {user?.role === 'admin' && (
                    <button
                      className="nx-dropdown-item"
                      onClick={() => {
                        navigate(`/${user.role}/business-settings`);
                        setProfileOpen(false);
                      }}
                    >
                      <i className="bi bi-gear"></i>
                      <span>Settings</span>
                    </button>
                  )}
                  {user?.role === 'patient' && (
                    <button
                      className="nx-dropdown-item"
                      onClick={() => {
                        navigate(`/${user.role}/profile`);
                        setProfileOpen(false);
                      }}
                    >
                      <i className="bi bi-person-circle"></i>
                      <span>My Profile</span>
                    </button>
                  )}
                  {(user?.role === 'doctor' || user?.role === 'staff') && (
                    <button
                      className="nx-dropdown-item"
                      onClick={() => {
                        navigate(`/${user.role}/settings`);
                        setProfileOpen(false);
                      }}
                    >
                      <i className="bi bi-gear"></i>
                      <span>Settings</span>
                    </button>
                  )}
                  <button className="nx-dropdown-item" onClick={toggleTheme}>
                    <i className={`bi ${isDark ? 'bi-sun-fill' : 'bi-moon-stars-fill'}`}></i>
                    <span>{isDark ? 'Light Mode' : 'Dark Mode'}</span>
                  </button>
                </div>
                <div className="nx-dropdown-divider"></div>
                <button className="nx-dropdown-item nx-logout-item" onClick={handleLogout}>
                  <i className="bi bi-box-arrow-right"></i>
                  <span>Sign Out</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
